import React, { useState, useEffect, useRef } from "react";
import { Provider } from "../../types";
import ProviderProfile from "./ProviderProfile";
import defaultAvatar from "../../assets/avatar.png";

interface ProvidersListProps {
  providers: Provider[];
  onClose: () => void;
  conversationId?: string;
}

const ProvidersList: React.FC<ProvidersListProps> = ({
  providers,
  onClose,
  conversationId = "",
}) => {
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(
    null
  );
  const [isExpanded, setIsExpanded] = useState(false);
  const [startY, setStartY] = useState<number>(0);
  const [currentY, setCurrentY] = useState<number>(0);
  const [isDragging, setIsDragging] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const handleAvatarClick = (provider: Provider) => {
    setSelectedProvider(provider);
  };

  const handleBack = () => {
    setSelectedProvider(null);
  };

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  // Touch handlers for swipe gesture
  const handleTouchStart = (e: React.TouchEvent) => {
    setStartY(e.touches[0].clientY);
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    setCurrentY(e.touches[0].clientY);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    const deltaY = currentY - startY;

    // If swiped down more than 100px, collapse
    if (deltaY > 100 && isExpanded) {
      setIsExpanded(false);
    }

    setIsDragging(false);
    setStartY(0);
    setCurrentY(0);
  };

  // Close panel on backdrop click (mobile only)
  const handleBackdropClick = () => {
    if (isExpanded) {
      setIsExpanded(false);
    }
  };

  if (selectedProvider) {
    return (
      <ProviderProfile
        provider={selectedProvider}
        onBack={handleBack}
        conversationId={conversationId}
      />
    );
  }

  // Don't display panel if no provider data
  if (!providers || providers.length === 0) {
    return null;
  }

  return (
    <>
      {/* Mobile backdrop */}
      {isExpanded && (
        <div className="providers-backdrop" onClick={handleBackdropClick} />
      )}

      {/* Collapsed pill for mobile */}
      <div
        className={`providers-pill ${isExpanded ? 'hidden' : ''}`}
        onClick={handleToggle}
      >
        <div className="pill-content">
          <div className="pill-avatars">
            {providers.slice(0, 3).map((provider, index) => {
              const profilePhoto = provider.profile_data?.photo_urls?.[0] || provider.avatar || defaultAvatar;
              return (
                <img
                  key={provider.id}
                  src={profilePhoto}
                  alt={provider.name}
                  style={{ zIndex: 3 - index, marginLeft: index > 0 ? '-8px' : '0' }}
                />
              );
            })}
          </div>
          <span className="pill-text">
            {providers.length} {providers.length === 1 ? 'Therapist' : 'Therapists'} Found
          </span>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M5 12L10 7L15 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      {/* Provider panel - sidebar on desktop, bottom sheet on mobile */}
      <div
        ref={panelRef}
        className={`providers-panel ${isExpanded ? 'expanded' : ''}`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Drag handle for mobile */}
        <div className="panel-drag-handle">
          <div className="drag-indicator"></div>
        </div>

        <div className="providers-header">
          <h3>Recommended Therapists</h3>
          <button className="close-button" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <div className="providers-list">
          {providers.map((provider, index) => {
            // Get profile photo from profile_data if available
            const profilePhoto = provider.profile_data?.photo_urls?.[0] || provider.avatar || defaultAvatar;

            // Get license/title from profile_data or use default
            const license = provider.profile_data?.title || provider.title || provider.type;

            // Only show first name to respect paywall (full details behind paywall)
            const firstName = provider.name.split(" ")[0] || provider.name;

            return (
              <div
                key={provider.id}
                className="provider-card"
                onClick={() => handleAvatarClick(provider)}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="provider-avatar clickable">
                  <img src={profilePhoto} alt={firstName} />
                  {provider.is_online && provider.is_registered && (
                    <div className="online-indicator"></div>
                  )}
                </div>
                <div className="provider-info">
                  <h4>{firstName}</h4>
                  <p className="provider-license">{license}</p>
                  {provider.is_online && provider.is_registered && (
                    <span className="availability-badge">Available Now</span>
                  )}
                </div>
                <svg className="chevron-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M7 4L13 10L7 16"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default ProvidersList;
